import {
  Box,
  Grid,
  Heading,
  VStack,
  Text,
  Card,
  CardBody,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Skeleton,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Flex,
  Badge,
} from '@chakra-ui/react'
import { PieChart as RechartsPieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts'
import { Users, Building2, Wrench, Shield, Database, Briefcase, Layers } from 'lucide-react'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useEffect, useState } from 'react'
import {
  dashboardService,
  AuthDashboardStats,
} from '@/lib/services/dashboardService'
import { AdminLayout } from '@/components/layout/AdminLayout'
import { useAuthStore } from '@/store/auth'
import { SecurityStatusCard } from '@/components/dashboard/SecurityStatusCard'
import { SystemSettingsCard } from '@/components/dashboard/SystemSettingsCard'
import { ModuleAnalyticsTable } from '@/components/dashboard/ModuleAnalyticsTable'
import { ResourceStatsCard } from '@/components/dashboard/ResourceStatsCard'
import { ClientsSummaryTable } from '@/components/dashboard/ClientsSummaryTable'
import { ActivityTrendsTabs } from '@/components/dashboard/ActivityTrendsTabs'
import { StatCardWithBreakdown } from '@/components/dashboard/StatCardWithBreakdown'

const ROLE_COLORS = ['#3182CE', '#38A169', '#DD6B20', '#805AD5', '#D69E2E', '#E53E3E', '#9F7AEA', '#ED64A6']

const defaultStats: Partial<AuthDashboardStats> = {
  clusters: {
    total: 0,
    active: 0,
    inactive: 0,
    deleted: 0,
    by_parent: [],
  },
  clients: {
    total: 0,
    active: 0,
    inactive: 0,
    deleted: 0,
    new_today: 0,
    new_this_week: 0,
    new_this_month: 0,
  },
  field_engineers: {
    total: 0,
    active: 0,
    inactive: 0,
    deleted: 0,
    new_today: 0,
    new_this_week: 0,
    new_this_month: 0,
    by_cluster: [],
    by_region: [],
  },
  users: {
    total: 0,
    active: 0,
    inactive: 0,
    deleted: 0,
    new_today: 0,
    new_this_week: 0,
    new_this_month: 0,
    new_last_30_days: 0,
    active_last_30_days: 0,
    by_role: [],
  },
  assigned_devices: {
    total: 0,
    active: 0,
    inactive: 0,
    deleted: 0,
    new_today: 0,
    new_this_week: 0,
    new_this_month: 0,
    by_user: [],
    by_status: [],
  },
  system_settings: {
    total: 0,
    key_settings: [],
    by_category: {
      security: [],
      email: [],
      general: [],
    },
  },
  security: {
    failed_logins_24h: 0,
    failed_logins_yesterday: 0,
    active_sessions: 0,
    locked_users: 0,
    disabled_users: 0,
    status: 'green',
    trend: 'stable',
  },
  clients_summary: [],
  activity_trends: [],
  user_registration_trends: [],
  hourly_trends: {
    today: Array(24).fill(0),
    yesterday: Array(24).fill(0),
  },
  monthly_module_activities_trends: {
    trends: [],
    per_module: [],
    top_modules: [],
    module_totals: {},
  },
  module_analytics: {
    per_module: [],
    total_today: 0,
    total_this_week: 0,
    total_this_month: 0,
  },
  roles: {
    total: 0,
    active: 0,
    inactive: 0,
    deleted: 0,
    by_role: [],
  },
  teams: {
    total: 0,
    active: 0,
    inactive: 0,
    deleted: 0,
    teams_with_members: [],
  },
  applications: {
    total: 0,
    active: 0,
    inactive: 0,
    deleted: 0,
  },
  recent_users: [],
}

export default function DashboardPage() {
  const colors = useThemeColors()
  const { user } = useAuthStore()
  const [authStats, setAuthStats] = useState<AuthDashboardStats | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const stats = await dashboardService.getAuthStats()
        if (stats) {
          setAuthStats({
            ...defaultStats,
            ...stats,
            clusters: stats.clusters || defaultStats.clusters!,
            clients: stats.clients || defaultStats.clients!,
            field_engineers: stats.field_engineers || defaultStats.field_engineers!,
            assigned_devices: stats.assigned_devices || defaultStats.assigned_devices!,
            system_settings: stats.system_settings || defaultStats.system_settings!,
            hourly_trends: stats.hourly_trends || { today: Array(24).fill(0), yesterday: Array(24).fill(0) },
            monthly_module_activities_trends: stats.monthly_module_activities_trends || defaultStats.monthly_module_activities_trends!,
            module_analytics: stats.module_analytics || defaultStats.module_analytics!,
            roles: stats.roles || defaultStats.roles!,
            teams: stats.teams || defaultStats.teams!,
            applications: stats.applications || defaultStats.applications!,
            clients_summary: stats.clients_summary || [],
            activity_trends: stats.activity_trends || [],
            user_registration_trends: stats.user_registration_trends || [],
            recent_users: stats.recent_users || [],
          } as AuthDashboardStats)
        }
      } catch (error) {
        console.warn('Auth dashboard stats not available:', error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchDashboardData()
  }, [])

  const roleDistributionData =
    authStats?.roles?.by_role && authStats.roles.by_role.length > 0
      ? authStats.roles.by_role.map((item, index) => ({
          name: item.role,
          value: item.count,
          color: ROLE_COLORS[index % ROLE_COLORS.length],
        }))
      : []

  // Prepare breakdown data for Field Engineers
  const fieldEngineerBreakdown = authStats?.field_engineers?.by_cluster?.map((item) => ({
    label: item.cluster || 'Unknown',
    count: item.count,
  })) || []

  // Prepare breakdown data for Assigned Devices
  const assignedDeviceBreakdown = authStats?.assigned_devices?.by_status?.map((item) => ({
    label: item.status,
    count: item.count,
  })) || []

  return (
    <AdminLayout>
      <Box p={4} h="100%" overflow="hidden" display="flex" flexDirection="column" w="100%" minW={0}>
        <VStack spacing={6} align="stretch" flex="1" overflowY="auto" overflowX="hidden" minH={0} w="100%">
          {/* Header */}
          <Box flexShrink={0}>
            <VStack align="flex-start" spacing={1} mb={2}>
              <Heading size="lg">Welcome back, {user?.name || user?.user_login}!</Heading>
              <Text color={colors.text} fontSize="sm">
                Overview of your authentication system and organization resources.
              </Text>
            </VStack>
          </Box>

          {/* System Health KPIs */}
          <Box flexShrink={0}>
            <Heading size="sm" color="gray.600" mb={3}>
              System Health
            </Heading>
            <Grid
              templateColumns={{
                base: '1fr',
                sm: 'repeat(2, 1fr)',
                lg: 'repeat(4, 1fr)',
              }}
              gap={4}
              w="100%"
              minW={0}
            >
              <StatCardWithBreakdown
                title="Total Users"
                value={authStats?.users?.total || 0}
                icon={Users}
                color="blue"
                isLoading={isLoading}
                breakdown={{
                  active: authStats?.users?.active,
                  inactive: authStats?.users?.inactive,
                  deleted: authStats?.users?.deleted,
                }}
              />
              <StatCardWithBreakdown
                title="Total Clients"
                value={authStats?.clients?.total || 0}
                icon={Building2}
                color="green"
                isLoading={isLoading}
                breakdown={{
                  active: authStats?.clients?.active,
                  inactive: authStats?.clients?.inactive,
                  deleted: authStats?.clients?.deleted,
                }}
              />
              <StatCardWithBreakdown
                title="Field Engineers"
                value={authStats?.field_engineers?.total || 0}
                icon={Wrench}
                color="orange"
                isLoading={isLoading}
                breakdown={{
                  active: authStats?.field_engineers?.active,
                  inactive: authStats?.field_engineers?.inactive,
                  deleted: authStats?.field_engineers?.deleted,
                }}
              />
              <Card bg="white" borderRadius="md" boxShadow="sm">
                <CardBody>
                  <Stat>
                    <StatLabel fontSize="xs" color="gray.600">
                      Security Status
                    </StatLabel>
                    {isLoading ? (
                      <Skeleton height="24px" width="80px" mt={1} />
                    ) : (
                      <Badge
                        colorScheme={
                          authStats?.security?.status === 'green'
                            ? 'green'
                            : authStats?.security?.status === 'yellow'
                            ? 'yellow'
                            : 'red'
                        }
                        fontSize="sm"
                        px={3}
                        py={1}
                        mt={2}
                      >
                        {authStats?.security?.status === 'green'
                          ? 'Secure'
                          : authStats?.security?.status === 'yellow'
                          ? 'Warning'
                          : 'Critical'}
                      </Badge>
                    )}
                    <StatHelpText mb={0} fontSize="xs" mt={2}>
                      Failed Logins: {authStats?.security?.failed_logins_24h || 0}
                    </StatHelpText>
                  </Stat>
                </CardBody>
              </Card>
            </Grid>
          </Box>

          {/* Organization Statistics */}
          <Box flexShrink={0}>
            <Heading size="sm" color="gray.600" mb={3}>
              Organization Overview
            </Heading>
            <Grid
              templateColumns={{
                base: '1fr',
                sm: 'repeat(2, 1fr)',
                lg: 'repeat(4, 1fr)',
              }}
              gap={4}
              w="100%"
              minW={0}
            >
              <StatCardWithBreakdown
                title="Clusters"
                value={authStats?.clusters?.total || 0}
                icon={Layers}
                color="purple"
                isLoading={isLoading}
                breakdown={{
                  active: authStats?.clusters?.active,
                  inactive: authStats?.clusters?.inactive,
                  deleted: authStats?.clusters?.deleted,
                }}
              />
              <StatCardWithBreakdown
                title="Teams"
                value={authStats?.teams?.total || 0}
                icon={Briefcase}
                color="teal"
                isLoading={isLoading}
                breakdown={{
                  active: authStats?.teams?.active,
                  inactive: authStats?.teams?.inactive,
                  deleted: authStats?.teams?.deleted,
                }}
              />
              <StatCardWithBreakdown
                title="Roles"
                value={authStats?.roles?.total || 0}
                icon={Shield}
                color="blue"
                isLoading={isLoading}
                breakdown={{
                  active: authStats?.roles?.active,
                  inactive: authStats?.roles?.inactive,
                  deleted: authStats?.roles?.deleted,
                }}
              />
              <StatCardWithBreakdown
                title="Applications"
                value={authStats?.applications?.total || 0}
                icon={Database}
                color="cyan"
                isLoading={isLoading}
                breakdown={{
                  active: authStats?.applications?.active,
                  inactive: authStats?.applications?.inactive,
                  deleted: authStats?.applications?.deleted,
                }}
              />
            </Grid>
          </Box>

          {/* Security & System Settings */}
          <Grid
            templateColumns={{
              base: '1fr',
              lg: '1fr 1fr',
            }}
            gap={4}
            flexShrink={0}
            w="100%"
            minW={0}
          >
            <SecurityStatusCard
              status={authStats?.security?.status || 'green'}
              trend={authStats?.security?.trend || 'stable'}
              failedLogins24h={authStats?.security?.failed_logins_24h || 0}
              failedLoginsYesterday={authStats?.security?.failed_logins_yesterday || 0}
              activeSessions={authStats?.security?.active_sessions || 0}
              lockedUsers={authStats?.security?.locked_users || 0}
              disabledUsers={authStats?.security?.disabled_users || 0}
              isLoading={isLoading}
            />
            <SystemSettingsCard
              total={authStats?.system_settings?.total || 0}
              keySettings={authStats?.system_settings?.key_settings || []}
              byCategory={authStats?.system_settings?.by_category || {
                security: [],
                email: [],
                general: [],
              }}
              isLoading={isLoading}
            />
          </Grid>

          {/* Activity Trends - Tabbed View */}
          <ActivityTrendsTabs
            moduleActivities={authStats?.monthly_module_activities_trends?.trends || []}
            perModuleData={authStats?.monthly_module_activities_trends?.per_module || []}
            userRegistrationTrends={authStats?.user_registration_trends || []}
            activityTrends={authStats?.activity_trends || []}
            hourlyTrends={authStats?.hourly_trends || { today: Array(24).fill(0), yesterday: Array(24).fill(0) }}
            isLoading={isLoading}
          />

          {/* Module Analytics & User Distribution */}
          <Grid
            templateColumns={{
              base: '1fr',
              lg: '1.5fr 1fr',
            }}
            gap={4}
            flexShrink={0}
            w="100%"
            minW={0}
          >
            <ModuleAnalyticsTable
              analytics={authStats?.module_analytics?.per_module || []}
              isLoading={isLoading}
            />
            {/* Role Distribution Pie Chart */}
            <Card bg="white" borderRadius="md" boxShadow="sm">
              <CardBody>
                <VStack spacing={4} align="stretch">
                  <Heading size="md">User Distribution by Role</Heading>
                  {isLoading ? (
                    <Box h="300px" display="flex" alignItems="center" justifyContent="center">
                      <Skeleton height="250px" width="250px" borderRadius="full" />
                    </Box>
                  ) : roleDistributionData.length > 0 ? (
                    <Box position="relative" h="300px" display="flex" alignItems="center" justifyContent="center">
                      <Box position="absolute" textAlign="center" zIndex={1}>
                        <Text fontSize="2xl" fontWeight="bold" color="blue.700">
                          {authStats?.users?.total ?? 0}
                        </Text>
                        <Text fontSize="sm" color="gray.500">
                          Total Users
                        </Text>
                      </Box>
                      <ResponsiveContainer width="100%" height="100%">
                        <RechartsPieChart>
                          <Pie
                            data={roleDistributionData}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            innerRadius={70}
                            outerRadius={100}
                            paddingAngle={2}
                            label={({ name, percent }) =>
                              percent > 0.05 ? `${name} ${(percent * 100).toFixed(0)}%` : ''
                            }
                          >
                            {roleDistributionData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip />
                          <Legend />
                        </RechartsPieChart>
                      </ResponsiveContainer>
                    </Box>
                  ) : (
                    <Box h="300px" display="flex" alignItems="center" justifyContent="center">
                      <Text color="gray.500">No role data available</Text>
                    </Box>
                  )}
                </VStack>
              </CardBody>
            </Card>
          </Grid>

          {/* Resource Statistics */}
          <Box flexShrink={0}>
            <Heading size="sm" color="gray.600" mb={3}>
              Resource Management
            </Heading>
            <Grid
              templateColumns={{
                base: '1fr',
                lg: 'repeat(2, 1fr)',
              }}
              gap={4}
              w="100%"
              minW={0}
            >
              <ResourceStatsCard
                title="Assigned Devices"
                total={authStats?.assigned_devices?.total || 0}
                active={authStats?.assigned_devices?.active || 0}
                inactive={authStats?.assigned_devices?.inactive || 0}
                deleted={authStats?.assigned_devices?.deleted || 0}
                newToday={authStats?.assigned_devices?.new_today || 0}
                newThisWeek={authStats?.assigned_devices?.new_this_week || 0}
                newThisMonth={authStats?.assigned_devices?.new_this_month || 0}
                breakdown={
                  assignedDeviceBreakdown.length > 0
                    ? {
                        type: 'status',
                        data: assignedDeviceBreakdown,
                      }
                    : undefined
                }
                isLoading={isLoading}
              />
              <ResourceStatsCard
                title="Field Engineers"
                total={authStats?.field_engineers?.total || 0}
                active={authStats?.field_engineers?.active || 0}
                inactive={authStats?.field_engineers?.inactive || 0}
                deleted={authStats?.field_engineers?.deleted || 0}
                newToday={authStats?.field_engineers?.new_today || 0}
                newThisWeek={authStats?.field_engineers?.new_this_week || 0}
                newThisMonth={authStats?.field_engineers?.new_this_month || 0}
                breakdown={
                  fieldEngineerBreakdown.length > 0
                    ? {
                        type: 'cluster',
                        data: fieldEngineerBreakdown,
                      }
                    : undefined
                }
                isLoading={isLoading}
              />
            </Grid>
          </Box>

          {/* Clients Summary */}
          <ClientsSummaryTable clients={authStats?.clients_summary || []} isLoading={isLoading} />

          {/* Recent Users */}
          <Card bg="white" borderRadius="md" boxShadow="sm">
            <CardBody>
              <Flex justify="space-between" align="center" mb={4}>
                <Heading size="md">Recent Users</Heading>
                <Text
                  as="a"
                  href="/access-control/users"
                  fontSize="sm"
                  color="blue.600"
                  _hover={{ textDecoration: 'underline' }}
                >
                  View All ↗
                </Text>
              </Flex>
              {isLoading ? (
                <Skeleton height="200px" width="100%" />
              ) : (
                <Table size="sm">
                  <Thead>
                    <Tr>
                      <Th>Name</Th>
                      <Th>Email</Th>
                      <Th>Status</Th>
                      <Th>Created</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {(authStats?.recent_users ?? []).length === 0 ? (
                      <Tr>
                        <Td colSpan={4} textAlign="center" color="gray.500" py={6}>
                          No recent users
                        </Td>
                      </Tr>
                    ) : (
                      (authStats?.recent_users ?? []).map((user) => (
                        <Tr key={user.id}>
                          <Td fontWeight="medium">{user.name}</Td>
                          <Td>{user.user_email}</Td>
                          <Td>
                            <Badge colorScheme={user.user_status ? 'green' : 'red'}>
                              {user.user_status ? 'Active' : 'Inactive'}
                            </Badge>
                          </Td>
                          <Td>
                            {user.created_at
                              ? new Date(user.created_at).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric',
                                })
                              : '—'}
                          </Td>
                        </Tr>
                      ))
                    )}
                  </Tbody>
                </Table>
              )}
            </CardBody>
          </Card>
        </VStack>
      </Box>
    </AdminLayout>
  )
}
